import {
  formatShortDateAndTimeOrSameDay,
  useCustomQuery,
} from '@gripp/shared-logic';
import {
  Colors,
  ColumnData,
  INTEGRATION_PROVIDER_NAMES,
  IntegrationRunAsset,
  IntegrationRunData,
  tokens,
  useSlidePanel,
} from '@gripp/shared-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import { useNavigate, useParams } from 'react-router-dom';

const INTEGRATION_RUN_PANEL_SOURCE =
  'gripp-workspace/apps/gripp-web/src/activity/components/integrationRunPanel.tsx';
const ACTIVITY_ROUTES_SOURCE =
  'gripp-workspace/apps/gripp-web/src/activity/routes.tsx';
const ACTIVITY_RUN_ROUTE_PATTERN = '/activities/sync-run/:runId';

/**
 * Fetch the per-run detail blob for one run (Plan 0019). `integration.runDetails`
 * reads `details.json` from blob storage and returns a self-contained object
 * ({ provider, ranAt, status, counts, assets }) — the unbounded per-asset
 * breakdown that is deliberately NOT stored on the Mongo record. Namespaced
 * under `integration`, so `useCustomQuery` does NOT auto-unwrap; read
 * `data.integration.runDetails` directly.
 */
const RUN_DETAILS = `
  query IntegrationRunDetails($id: ID!) {
    integration {
      runDetails(id: $id)
    }
  }
`;

/** Shape of the details.json blob returned by integration.runDetails. */
type RunDetailsBlob = Pick<
  IntegrationRunData,
  'provider' | 'ranAt' | 'status' | 'counts' | 'assets'
>;

type RunDetailsResponse = {
  integration?: { runDetails?: RunDetailsBlob | null };
};

/**
 * Right-hand SlideLayout detail panel for a single integration sync run (web).
 *
 * Tapping a per-run card in the feed navigates to `activities/sync-run/:runId`,
 * which renders this through the activity `<Outlet>`. It registers the right
 * column via `useSlidePanel('right', …)` — the same mechanism the conversation
 * panel uses — so the detail slides in with the standard transition + back
 * button. Body: a counts summary line plus the per-asset breakdown. (Mobile
 * shows this same breakdown by expanding the card in place instead.)
 */
export const IntegrationRunPanel = () => {
  const { runId } = useParams();
  const navigate = useNavigate();

  const { data, isFetching } = useCustomQuery({
    query: RUN_DETAILS,
    variables: { id: runId ?? '' },
    cacheKey: ['integrationRunDetails', runId ?? ''],
    options: { enabled: !!runId },
  }) as { data?: RunDetailsResponse; isFetching: boolean };

  const run = data?.integration?.runDetails ?? undefined;

  const rightColumn = useMemo<ColumnData | null>(() => {
    if (!runId) return null;
    return {
      title: 'integrations.run.detailTitle',
      showHeader: true,
      showBackButton: true,
      onBackButtonPress: () => navigate('/activities'),
      disableScroll: false,
      mainComponent: <RunDetail run={run} isFetching={isFetching} />,
      debug: {
        id: 'activity-sync-run:right-detail',
        titleKey: 'integrations.run.detailTitle',
        component: 'IntegrationRunPanel',
        componentSource: INTEGRATION_RUN_PANEL_SOURCE,
        columnSource: INTEGRATION_RUN_PANEL_SOURCE,
        ownerComponent: 'IntegrationRunPanel',
        ownerSource: INTEGRATION_RUN_PANEL_SOURCE,
        routePanelKey: 'activity-sync-run',
        routePattern: ACTIVITY_RUN_ROUTE_PATTERN,
        routeSource: ACTIVITY_ROUTES_SOURCE,
        entity: { type: 'integrationRun', id: runId },
      },
    };
  }, [runId, run, isFetching, navigate]);

  useSlidePanel('right', rightColumn, [rightColumn]);

  return null;
};

type RunDetailProps = { run?: RunDetailsBlob; isFetching?: boolean };

const RunDetail = ({ run, isFetching }: RunDetailProps) => {
  const { t } = useTranslation();

  // No run means either the details.json blob is still loading or the run is
  // unknown / has no detail. While fetching, render nothing (the panel slides
  // in blank briefly). Once fetching settles with no run, show the empty state
  // rather than a permanently blank panel body.
  if (!run) {
    if (isFetching) return null;
    return (
      <View style={styles.emptyState}>
        <Text style={styles.emptyText}>
          {t('integrations.run.detailEmpty')}
        </Text>
      </View>
    );
  }

  const assets: IntegrationRunAsset[] = run.assets ?? [];
  const assetsSynced = run.counts?.assetsMatched ?? assets.length ?? 0;
  const readingsWritten = run.counts?.readingsWritten ?? 0;
  const faults =
    (run.counts?.issuesOpened ?? 0) + (run.counts?.issuesUpdated ?? 0);
  const isFailed = (run.status as string) === 'FAILED';

  const summarySegments = [
    t('integrations.digest.readingsCount', { count: readingsWritten }),
    ...(faults > 0
      ? [t('integrations.digest.faultsCount', { count: faults })]
      : []),
  ];

  return (
    <View style={styles.container}>
      <View style={styles.summary}>
        <Text style={styles.summaryTitle}>
          {t('integrations.digest.titleSegment', {
            provider: INTEGRATION_PROVIDER_NAMES[run.provider] ?? run.provider,
            count: assetsSynced,
          })}
        </Text>
        <Text style={styles.summaryMeta}>
          {run.ranAt ? formatShortDateAndTimeOrSameDay(run.ranAt) : ''}
          {summarySegments.length ? ` · ${summarySegments.join(' · ')}` : ''}
        </Text>
        {isFailed && (
          <Text style={styles.failedText}>
            {t('integrations.digest.failedDescription')}
          </Text>
        )}
      </View>

      {assets.length === 0 ? (
        <View style={styles.emptyState}>
          <Text style={styles.emptyText}>
            {t('integrations.run.detailEmpty')}
          </Text>
        </View>
      ) : (
        <View style={styles.list}>
          {assets.map((asset) => (
            <View
              key={asset.assetId}
              testID={`integration-sync-run-asset-${asset.assetId}`}
              style={styles.assetRow}
            >
              <View style={styles.assetNameColumn}>
                <Text style={styles.assetName} numberOfLines={1}>
                  {asset.assetName || asset.assetId}
                </Text>
              </View>
              <Text style={styles.assetMeta}>
                {t('integrations.digest.readingsCount', {
                  count: asset.readingsWritten,
                })}
                {asset.issueCount > 0
                  ? ` · ${t('integrations.digest.faultsCount', {
                      count: asset.issueCount,
                    })}`
                  : ''}
              </Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  summary: {
    paddingVertical: tokens.spacing.md,
    paddingHorizontal: tokens.spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: Colors.role.borderSubtle,
    gap: tokens.spacing.xs,
  },
  summaryTitle: {
    ...tokens.typography.label.largeEmphasised,
    color: Colors.role.textStrong,
  },
  summaryMeta: {
    ...tokens.typography.label.medium,
    color: Colors.role.textSecondary,
  },
  failedText: {
    ...tokens.typography.label.medium,
    // A failed sync is a failure, not a caution. `warningDark` was also
    // 2.30:1 on this white panel; `textDestructive` is 6.45:1.
    color: Colors.role.textDestructive,
  },
  list: {
    paddingVertical: tokens.spacing.xs,
  },
  assetRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: tokens.spacing.md,
    paddingHorizontal: tokens.spacing.lg,
    gap: tokens.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: Colors.role.borderSubtle,
  },
  assetNameColumn: {
    flex: 1,
    minWidth: 0,
  },
  assetName: {
    ...tokens.typography.label.largeEmphasised,
    color: Colors.role.textPrimary,
  },
  assetMeta: {
    ...tokens.typography.label.medium,
    color: Colors.role.textSecondary,
    flexShrink: 0,
  },
  emptyState: {
    padding: tokens.spacing.lg,
  },
  emptyText: {
    ...tokens.typography.label.medium,
    color: Colors.role.textSecondary,
  },
});
